import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { 
  Gavel, Package, Clock, ArrowLeft, CheckCircle, XCircle, 
  MessageCircle, DollarSign, Calendar, ChevronRight, ImageOff,
  TrendingUp, AlertCircle, RefreshCw, Eye, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCurrencySymbol } from '@/utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyBidsPage() {
  const { user } = useAuth();
  const [bids, setBids] = useState([]);
  const [listings, setListings] = useState({});
  const [sellers, setSellers] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bids/my-bids`);
      setBids(response.data);

      const listingIds = [...new Set(response.data.map((bid) => bid.listing_id))];
      const listingPromises = listingIds.map((id) => 
        axios.get(`${API_URL}/api/listings/${id}`).catch(() => null)
      );
      const listingResponses = await Promise.all(listingPromises);

      const listingsMap = {};
      const sellerIds = new Set();
      
      listingResponses.forEach((res) => {
        if (res?.data) {
          listingsMap[res.data.id] = res.data;
          sellerIds.add(res.data.user_id);
        }
      });
      setListings(listingsMap);

      // Fetch seller info
      const sellersMap = {};
      for (const sellerId of sellerIds) {
        try {
          const sellerRes = await axios.get(`${API_URL}/api/users/${sellerId}`);
          sellersMap[sellerId] = sellerRes.data;
        } catch (err) {
          console.warn(`Failed to fetch seller ${sellerId}:`, err);
        }
      }
      setSellers(sellersMap);
    } catch (error) {
      console.error('Failed to fetch bids', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    total: bids.length,
    submitted: bids.filter(b => b.status === 'submitted').length,
    countered: bids.filter(b => b.status === 'countered').length,
    accepted: bids.filter(b => b.status === 'accepted').length,
    rejected: bids.filter(b => b.status === 'rejected').length,
    totalValue: bids.reduce((sum, b) => sum + (b.money_amount || 0), 0),
  };

  // Filter bids based on active tab
  const filteredBids = activeTab === 'all' 
    ? bids 
    : bids.filter(b => b.status === activeTab);

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-500',
      countered: 'bg-amber-500',
      accepted: 'bg-emerald-500',
      rejected: 'bg-red-500',
      withdrawn: 'bg-slate-400',
      not_selected: 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-400';
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-700 border-blue-200',
      countered: 'bg-amber-100 text-amber-700 border-amber-200',
      accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      withdrawn: 'bg-slate-100 text-slate-600 border-slate-200',
      not_selected: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      submitted: TrendingUp,
      countered: MessageCircle,
      accepted: CheckCircle,
      rejected: XCircle,
      withdrawn: RefreshCw,
      not_selected: AlertCircle
    };
    return icons[status] || AlertCircle;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAmount = (amount, country) => {
    if (!amount && amount !== 0) return '';
    const symbol = getCurrencySymbol(country);
    return `${symbol}${parseFloat(amount).toLocaleString()}`;
  };

  const tabs = [
    { id: 'all', label: 'All Bids', count: stats.total },
    { id: 'submitted', label: 'Pending', count: stats.submitted },
    { id: 'countered', label: 'Countered', count: stats.countered },
    { id: 'accepted', label: 'Accepted', count: stats.accepted },
    { id: 'rejected', label: 'Rejected', count: stats.rejected },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-2 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Bids Dashboard
            </h1>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
          >
            <Gavel className="w-5 h-5" />
            Browse & Bid
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Gavel className="w-5 h-5 text-slate-600" />
              </div>
              <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
            </div>
            <p className="text-sm text-slate-500">Total Bids</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-blue-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.submitted}</span>
            </div>
            <p className="text-sm text-slate-500">Pending</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-amber-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-2xl font-bold text-amber-600">{stats.countered}</span>
            </div>
            <p className="text-sm text-slate-500">Countered</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-emerald-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-2xl font-bold text-emerald-600">{stats.accepted}</span>
            </div>
            <p className="text-sm text-slate-500">Accepted</p>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-red-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <span className="text-2xl font-bold text-red-500">{stats.rejected}</span>
            </div>
            <p className="text-sm text-slate-500">Rejected</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">${stats.totalValue.toLocaleString()}</span>
            </div>
            <p className="text-sm text-emerald-100">Cash Offered</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Bids List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : filteredBids.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <Gavel className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {activeTab === 'all' ? 'No bids yet' : `No ${activeTab} bids`}
            </h3>
            <p className="text-slate-600 mb-6">
              {activeTab === 'all' ? 'Start bidding on listings to track them here' : 'Check other tabs or place more bids'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
            >
              <Gavel className="w-5 h-5" />
              Browse Listings
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBids.map((bid) => {
              const listing = listings[bid.listing_id];
              const seller = listing ? sellers[listing.user_id] : null;
              const StatusIcon = getStatusIcon(bid.status);

              return (
                <div
                  key={bid.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`bid-card-${bid.id}`}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Listing Image */}
                    <Link 
                      to={`/listing/${bid.listing_id}`}
                      className="sm:w-48 md:w-64 h-48 sm:h-auto flex-shrink-0 relative"
                    >
                      {listing?.images && listing.images.length > 0 ? (
                        <img 
                          src={listing.images[0]} 
                          alt={listing?.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <ImageOff className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      {/* Status indicator */}
                      <div className={`absolute top-3 left-3 w-3 h-3 rounded-full ${getStatusColor(bid.status)} ring-2 ring-white`} />
                    </Link>

                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Link 
                              to={`/listing/${bid.listing_id}`}
                              className="text-lg font-semibold text-slate-900 hover:text-indigo-600 transition-colors line-clamp-1"
                            >
                              {listing?.title || 'Loading...'}
                            </Link>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadge(bid.status)}`}>
                              <StatusIcon className="w-3 h-3" />
                              {bid.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          {seller && (
                            <p className="text-sm text-slate-500">
                              Seller: <span className="text-slate-700">{seller.name}</span>
                              {seller.city && ` • ${seller.city}`}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Bid Details Row */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Package className="w-3.5 h-3.5" />
                            Items Offered
                          </div>
                          <p className="text-lg font-bold text-indigo-600">{bid.items?.length || 0}</p>
                        </div>
                        
                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            Cash Offered
                          </div>
                          <p className="text-lg font-bold text-emerald-600">{formatAmount(bid.money_amount, listing?.country)}</p>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Bid Date
                          </div>
                          <p className="text-sm font-semibold text-slate-700">{formatDate(bid.created_at)}</p>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                            <Package className="w-3.5 h-3.5" />
                            Category
                          </div>
                          <p className="text-sm font-semibold text-slate-700 truncate">{listing?.category || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Items Offered Preview */}
                      {bid.items && bid.items.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-slate-500 mb-2">Your Offer:</p>
                          <div className="flex flex-wrap gap-2">
                            {bid.items.slice(0, 3).map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-1.5">
                                {item.images?.[0] ? (
                                  <img src={item.images[0]} alt={item.title} className="w-6 h-6 rounded object-cover" />
                                ) : (
                                  <Package className="w-4 h-4 text-indigo-400" />
                                )}
                                <span className="text-sm text-indigo-700 font-medium">{item.title}</span>
                              </div>
                            ))}
                            {bid.items.length > 3 && (
                              <div className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-600">
                                +{bid.items.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Counter Offer Alert */}
                      {bid.status === 'countered' && (bid.counter_message || bid.counter_amount) && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1">
                            <MessageCircle className="w-4 h-4" />
                            Counter Offer Received
                          </div>
                          {bid.counter_amount > 0 && (
                            <p className="text-amber-800 font-semibold">
                              Suggested: {formatAmount(bid.counter_amount, listing?.country)}
                            </p>
                          )}
                          {bid.counter_message && (
                            <p className="text-amber-700 text-sm mt-1">{bid.counter_message}</p>
                          )}
                        </div>
                      )}

                      {/* Accepted Alert */}
                      {bid.status === 'accepted' && (
                        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                          <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Congratulations! Your bid was accepted. Check your Trades to complete the exchange.
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                        <Link
                          to={`/listing/${bid.listing_id}`}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Listing
                        </Link>
                        
                        <Link
                          to={`/chat/${bid.id}`}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                          <Send className="w-4 h-4" />
                          Message Seller
                        </Link>

                        {bid.status === 'accepted' && (
                          <Link
                            to="/trades"
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors ml-auto"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Go to Trades
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        )}

                        {bid.status === 'countered' && (
                          <Link
                            to={`/listing/${bid.listing_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors ml-auto"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Respond to Counter
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
